import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";
import { snackSuccess, snackError } from '../../../../../actions/snackbarActions';
import { errorMessages } from '../../../../../Helpers/ErrorMessages';

/* ═══════════════════════════════════════════════════════════════
   useGynaeConsultation — Custom hook for Gynaecological
   Consultation Module (Task 18).
   Manages consultation form, menstrual history calculator,
   examination findings, diagnosis with ICD-10, and history
   timeline.
   ═══════════════════════════════════════════════════════════════ */

/* ─── Constants ─── */

export const MENSTRUAL_REGULARITY = [
    { value: "regular", label: "Regular" },
    { value: "irregular", label: "Irregular" },
    { value: "amenorrhea", label: "Amenorrhea" },
    { value: "post_menopausal", label: "Post-Menopausal" },
];

export const MENSTRUAL_FLOW = [
    { value: "light", label: "Light" },
    { value: "normal", label: "Normal" },
    { value: "heavy", label: "Heavy" },
    { value: "very_heavy", label: "Very Heavy (menorrhagia)" },
];

export const DYSMENORRHEA_SEVERITY = [
    { value: "none", label: "None" },
    { value: "mild", label: "Mild" },
    { value: "moderate", label: "Moderate" },
    { value: "severe", label: "Severe" },
];

export const SEXUAL_HISTORY_OPTIONS = [
    { value: "sexually_active", label: "Sexually Active" },
    { value: "not_sexually_active", label: "Not Sexually Active" },
    { value: "single_partner", label: "Single Partner" },
    { value: "multiple_partners", label: "Multiple Partners" },
    { value: "dyspareunia", label: "Dyspareunia (Pain During Intercourse)" },
    { value: "post_coital_bleeding", label: "Post-Coital Bleeding" },
];

export const CONTRACEPTIVE_HISTORY_OPTIONS = [
    { value: "none", label: "None / Never Used" },
    { value: "coc", label: "Combined Oral Contraceptives" },
    { value: "pop", label: "Progestin-Only Pills" },
    { value: "injectable", label: "Injectable (DMPA / NET-EN)" },
    { value: "implant", label: "Implant (Jadelle / Implanon)" },
    { value: "iud", label: "IUD (Copper / Hormonal)" },
    { value: "condoms", label: "Condoms" },
    { value: "tubal_ligation", label: "Tubal Ligation" },
    { value: "natural_methods", label: "Natural Methods (SDM / LAM)" },
    { value: "emergency_contraception", label: "Emergency Contraception" },
];

export const PAST_GYNAE_CONDITIONS = [
    { value: "fibroids", label: "Uterine Fibroids" },
    { value: "endometriosis", label: "Endometriosis" },
    { value: "ovarian_cyst", label: "Ovarian Cyst" },
    { value: "pcos", label: "Polycystic Ovary Syndrome (PCOS)" },
    { value: "pid", label: "Pelvic Inflammatory Disease (PID)" },
    { value: "cervical_dysplasia", label: "Cervical Dysplasia / Abnormal Pap" },
    { value: "cervical_cancer", label: "Cervical Cancer" },
    { value: "breast_cancer", label: "Breast Cancer" },
    { value: "ovarian_cancer", label: "Ovarian Cancer" },
    { value: "ectopic_pregnancy", label: "Ectopic Pregnancy" },
    { value: "molar_pregnancy", label: "Molar Pregnancy" },
    { value: "miscarriage", label: "Miscarriage / Spontaneous Abortion" },
    { value: "induced_abortion", label: "Induced Abortion" },
    { value: "post_abortion_care", label: "Post-Abortion Care (PAC)" },
    { value: "gynae_surgery", label: "Previous Gynaecological Surgery" },
    { value: "sti_history", label: "History of STI" },
    { value: "infertility_treatment", label: "Infertility Treatment" },
    { value: "prolapse", label: "Pelvic Organ Prolapse" },
    { value: "urinary_incontinence", label: "Urinary Incontinence" },
    { value: "none", label: "None" },
];

export const EXAMINATION_SECTIONS = {
    general: {
        label: "General Examination",
        icon: "",
        fields: [
            { key: "general_appearance", label: "General Appearance", type: "text" },
            { key: "pallor", label: "Pallor", type: "select", options: ["none", "mild", "moderate", "severe"] },
            { key: "jaundice", label: "Jaundice", type: "select", options: ["absent", "present"] },
            { key: "lymphadenopathy", label: "Lymphadenopathy", type: "select", options: ["none", "inguinal", "supraclavicular", "generalized"] },
            { key: "thyroid", label: "Thyroid", type: "select", options: ["normal", "enlarged", "nodular"] },
            { key: "bmi", label: "BMI", type: "number" },
        ],
    },
    breast: {
        label: "Breast Examination",
        icon: "",
        fields: [
            { key: "breast_inspection", label: "Inspection", type: "text", placeholder: "Symmetry, skin changes, nipple discharge" },
            { key: "breast_palpation_right", label: "Right Breast Palpation", type: "text" },
            { key: "breast_palpation_left", label: "Left Breast Palpation", type: "text" },
            { key: "axillary_nodes", label: "Axillary Lymph Nodes", type: "select", options: ["not_palpable", "palpable_right", "palpable_left", "palpable_bilateral"] },
            { key: "breast_finding", label: "Overall Breast Finding", type: "select", options: ["normal", "lump", "tenderness", "discharge", "skin_changes", "abnormal"] },
        ],
    },
    abdominal: {
        label: "Abdominal Examination",
        icon: "",
        fields: [
            { key: "abdomen_inspection", label: "Inspection", type: "text", placeholder: "Distension, scars, masses" },
            { key: "abdomen_palpation", label: "Palpation", type: "text", placeholder: "Tenderness, masses, organomegaly" },
            { key: "abdomen_tenderness", label: "Tenderness", type: "select", options: ["none", "suprapubic", "right_iliac_fossa", "left_iliac_fossa", "generalized"] },
            { key: "abdomen_mass", label: "Mass", type: "select", options: ["none", "pelvic_mass", "suprapubic_mass", "adnexal_mass", "other"] },
            { key: "abdomen_mass_size", label: "Mass Size (if present)", type: "text", placeholder: "e.g. 10cm, at umbilicus level" },
        ],
    },
    speculum: {
        label: "Speculum Examination",
        icon: "",
        fields: [
            { key: "vulva", label: "Vulva", type: "text", placeholder: "Lesions, atrophy, discharge, swelling" },
            { key: "vagina", label: "Vagina", type: "text", placeholder: "Discharge, lesions, prolapse" },
            { key: "cervix_appearance", label: "Cervix Appearance", type: "text", placeholder: "Healthy, erosion, polyp, suspicious lesion" },
            { key: "cervix_os", label: "Cervical Os", type: "select", options: ["closed", "open", "stenosed"] },
            { key: "discharge_type", label: "Discharge", type: "select", options: ["none", "clear", "white", "yellow", "green", "bloody", "foul_smelling"] },
            { key: "cervical_motion_tenderness", label: "Cervical Motion Tenderness", type: "select", options: ["absent", "present"] },
            { key: "via_result", label: "VIA Result (if done)", type: "select", options: ["not_done", "negative", "positive", "suspicious"] },
        ],
    },
    bimanual: {
        label: "Bimanual Examination",
        icon: "",
        fields: [
            { key: "uterus_size", label: "Uterus Size", type: "select", options: ["normal", "bulky", "enlarged_6wk", "enlarged_8wk", "enlarged_10wk", "enlarged_12wk", "enlarged_14wk", "enlarged_16wk", "enlarged_above_16wk", "atrophic"] },
            { key: "uterus_position", label: "Uterus Position", type: "select", options: ["anteverted", "retroverted", "mid_position", "fixed"] },
            { key: "uterus_consistency", label: "Uterus Consistency", type: "select", options: ["normal", "soft", "firm", "hard", "irregular"] },
            { key: "uterus_mobility", label: "Uterus Mobility", type: "select", options: ["mobile", "restricted", "fixed"] },
            { key: "uterus_tenderness", label: "Uterus Tenderness", type: "select", options: ["non_tender", "tender"] },
            { key: "right_adnexa", label: "Right Adnexa", type: "text", placeholder: "Normal / mass / tenderness" },
            { key: "left_adnexa", label: "Left Adnexa", type: "text", placeholder: "Normal / mass / tenderness" },
            { key: "pouch_of_douglas", label: "Pouch of Douglas", type: "select", options: ["free", "full", "tender", "mass"] },
        ],
    },
};

export const COMMON_GYNAE_DIAGNOSES = [
    { code: "N92.0", label: "Excessive and frequent menstruation with regular cycle" },
    { code: "N92.1", label: "Excessive and frequent menstruation with irregular cycle" },
    { code: "N91.2", label: "Amenorrhea, unspecified" },
    { code: "N94.6", label: "Dysmenorrhea, unspecified" },
    { code: "N73.0", label: "Acute pelvic inflammatory disease" },
    { code: "N73.1", label: "Chronic pelvic inflammatory disease" },
    { code: "N76.0", label: "Acute vaginitis" },
    { code: "N77.1", label: "Candidal vulvovaginitis" },
    { code: "D25.9", label: "Leiomyoma of uterus, unspecified (Fibroids)" },
    { code: "N80.0", label: "Endometriosis of uterus" },
    { code: "N83.2", label: "Ovarian cyst, unspecified" },
    { code: "E28.2", label: "Polycystic ovarian syndrome (PCOS)" },
    { code: "N87.1", label: "Moderate cervical dysplasia (CIN II)" },
    { code: "C53.9", label: "Cervical cancer, unspecified" },
    { code: "N81.1", label: "Cystocele" },
    { code: "N81.2", label: "Incomplete uterovaginal prolapse" },
    { code: "N81.3", label: "Complete uterovaginal prolapse" },
    { code: "N97.9", label: "Female infertility, unspecified" },
    { code: "N95.1", label: "Menopausal and female climacteric states" },
    { code: "N63", label: "Unspecified lump in breast" },
    { code: "N39.0", label: "Urinary tract infection, site not specified" },
    { code: "A64", label: "Unspecified sexually transmitted disease" },
    { code: "N75.0", label: "Bartholin abscess" },
    { code: "N84.0", label: "Polyp of corpus uteri (Endometrial polyp)" },
    { code: "N84.1", label: "Polyp of cervix uteri (Cervical polyp)" },
    { code: "O00.9", label: "Ectopic pregnancy, unspecified" },
    { code: "O02.1", label: "Missed abortion" },
    { code: "O03.9", label: "Complete spontaneous abortion" },
];

export const INVESTIGATION_TYPES = [
    { value: "fbc", label: "Full Blood Count (FBC)" },
    { value: "blood_group", label: "Blood Group & Rhesus" },
    { value: "hiv", label: "HIV Test" },
    { value: "vdrl", label: "VDRL / RPR (Syphilis)" },
    { value: "hep_b", label: "Hepatitis B Surface Antigen" },
    { value: "urinalysis", label: "Urinalysis" },
    { value: "urine_culture", label: "Urine Culture & Sensitivity" },
    { value: "hvs", label: "High Vaginal Swab (HVS)" },
    { value: "endocervical_swab", label: "Endocervical Swab" },
    { value: "pap_smear", label: "Pap Smear / Cervical Cytology" },
    { value: "pregnancy_test", label: "Pregnancy Test (β-hCG)" },
    { value: "serum_bhcg", label: "Serum β-hCG (quantitative)" },
    { value: "hormonal_profile", label: "Hormonal Profile (FSH, LH, E2, Progesterone)" },
    { value: "thyroid_function", label: "Thyroid Function Tests" },
    { value: "prolactin", label: "Prolactin Level" },
    { value: "ca125", label: "CA-125" },
    { value: "pelvic_ultrasound", label: "Pelvic Ultrasound" },
    { value: "transvaginal_ultrasound", label: "Transvaginal Ultrasound" },
    { value: "breast_ultrasound", label: "Breast Ultrasound" },
    { value: "mammography", label: "Mammography" },
    { value: "hsg", label: "Hysterosalpingogram (HSG)" },
    { value: "ct_pelvis", label: "CT Pelvis" },
    { value: "mri_pelvis", label: "MRI Pelvis" },
    { value: "endometrial_biopsy", label: "Endometrial Biopsy" },
    { value: "cervical_biopsy", label: "Cervical Biopsy" },
    { value: "colposcopy", label: "Colposcopy" },
    { value: "other", label: "Other" },
];

export const PROCEDURE_OPTIONS = [
    { value: "none", label: "No Procedure" },
    { value: "iud_insertion", label: "IUD Insertion" },
    { value: "iud_removal", label: "IUD Removal" },
    { value: "implant_insertion", label: "Implant Insertion" },
    { value: "implant_removal", label: "Implant Removal" },
    { value: "cervical_polypectomy", label: "Cervical Polypectomy" },
    { value: "endometrial_biopsy", label: "Endometrial Biopsy" },
    { value: "cervical_biopsy", label: "Cervical Biopsy" },
    { value: "cryotherapy", label: "Cryotherapy" },
    { value: "leep", label: "LEEP / LLETZ" },
    { value: "thermocoagulation", label: "Thermocoagulation" },
    { value: "bartholin_abscess_drainage", label: "Bartholin Abscess I&D" },
    { value: "marsupialisation", label: "Marsupialisation" },
    { value: "eua", label: "Examination Under Anaesthesia (EUA)" },
    { value: "d_and_c", label: "Dilatation & Curettage (D&C)" },
    { value: "hysteroscopy", label: "Hysteroscopy" },
    { value: "laparoscopy", label: "Laparoscopy" },
    { value: "mvr", label: "Manual Vacuum Aspiration (MVA)" },
    { value: "vulvar_biopsy", label: "Vulconst Biopsy" },
    { value: "other", label: "Other Procedure" },
];

/* ─── Helpers ─── */
const todayDate = function () {
    const d = new Date();
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

const calculateCycleInfo = function (lmp, cycleLength) {
    if (!lmp) return null;
    const lmpDate = new Date(lmp);
    const today = new Date();
    const diffMs = today.getTime() - lmpDate.getTime();
    const daysSinceLMP = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const cl = parseInt(cycleLength, 10) || 28;
    const expectedNextPeriod = new Date(lmpDate);
    expectedNextPeriod.setDate(expectedNextPeriod.getDate() + cl);
    const daysLate = Math.floor((today.getTime() - expectedNextPeriod.getTime()) / (1000 * 60 * 60 * 24));
    return {
        daysSinceLMP: daysSinceLMP,
        expectedNextPeriod: expectedNextPeriod.toISOString().slice(0, 10),
        daysLate: daysLate > 0 ? daysLate : 0,
        isLate: daysLate > 0,
        cycleDay: (daysSinceLMP % cl) + 1,
    };
};

/* ─── Required Fields Configuration ─── */
export const REQUIRED_FIELDS = {
    presenting_complaint: { label: "Presenting complaint", section: "examination" },
    lmp: { label: "Last menstrual period", section: "history" },
    regularity: { label: "Menstrual regularity", section: "history" },
};

/* ─── Initial Form ─── */
const INITIAL_FORM = {
    presenting_complaint: "",
    /* Menstrual history */
    menarche_age: "",
    cycle_length: "28",
    period_duration: "5",
    regularity: "",
    flow_amount: "",
    dysmenorrhea: "none",
    lmp: "",
    intermenstrual_bleeding: false,
    post_coital_bleeding: false,
    /* Obstetric summary (GPAL) */
    gravida: "",
    para: "",
    abortions: "",
    living_children: "",
    /* Sexual history */
    sexual_history: [],
    age_at_first_intercourse: "",
    /* Contraceptive history */
    contraceptive_current: "",
    contraceptive_past: [],
    /* Past gynaecological history */
    past_gynae_history: [],
    past_gynae_details: "",
    /* Examination (free-text clinical notes) */
    physical_examination: "",
    past_medical_history: "",
    past_obstetric_history: "",
    family_history: "",
    notes: "",
    /* Examination findings (legacy JSON accordion — kept for backwards compat) */
    examination: {},
    /* Diagnosis */
    provisional_diagnosis: "",
    diagnosis_icd10_code: "",
    diagnosis_icd10_label: "",
    differential_diagnoses: "",
    /* Investigations */
    investigations_ordered: [],
    investigation_notes: "",
    /* Treatment / Plan */
    treatment_plan: "",
    procedure_performed: "none",
    procedure_details: "",
    medications_prescribed: "",
    /* Follow-up */
    follow_up_plan: "",
    follow_up_date: "",
    referral_needed: false,
    referral_to: "",
    referral_reason: "",
};

/* ═══ HOOK ═══ */
export const useGynaeConsultation = function (actions, patient_number, visit_id, user_roles_id) {

    /* ─── Consultation records ─── */
    const [consultations, setConsultations] = useState([]);

    /* ─── Form state ─── */
    const [form, setForm] = useState(Object.assign({}, INITIAL_FORM));

    /* ─── Editing ─── */
    const [editingRecord, setEditingRecord] = useState(null);

    /* ─── UI state ─── */
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("consultation");

    /* Active form section for wizard-like progression */
    const [activeSection, setActiveSection] = useState("history");

    /* ─── Validation state ─── */
    const [validationErrors, setValidationErrors] = useState({});

    /* Validate all required fields; returns true if form is valid */
    const validateForm = useCallback(function () {
        const errors = {};
        Object.keys(REQUIRED_FIELDS).forEach(function (fieldName) {
            const value = form[fieldName];
            if (value === undefined || value === null || value === "") {
                errors[fieldName] = REQUIRED_FIELDS[fieldName].label + " is required";
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [form]);

    /* Clear a single field error (called when user types in a field) */
    const clearFieldError = useCallback(function (fieldName) {
        setValidationErrors(function (prev) {
            if (!prev[fieldName]) return prev;
            const next = Object.assign({}, prev);
            delete next[fieldName];
            return next;
        });
    }, []);

    /* ─── Fetch data ─── */
    useEffect(function () {
        if (!patient_number || !visit_id) return;
        setLoading(true);
        axios.get(config.smsUrl + "/obgyn/gynae/patient/" + patient_number + "/1")
            .then(function (res) {
                const data = res.data || {};
                const records = Array.isArray(data) ? data : (data.consultations || []);
                setConsultations(records);
            })
            .catch(function () { })
            .finally(function () { setLoading(false); });
    }, [patient_number, visit_id]);

    /* ─── Form change handler ─── */
    const handleChange = useCallback(function (e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function (prev) {
            const next = Object.assign({}, prev);
            next[name] = value;
            return next;
        });
        /* Clear validation error for this field when user provides a value */
        if (REQUIRED_FIELDS[name] && value !== "" && value !== null && value !== undefined) {
            clearFieldError(name);
        }
    }, [clearFieldError]);

    /* ─── Examination field change ─── */
    const handleExamChange = useCallback(function (section, field, value) {
        setForm(function (prev) {
            const examination = Object.assign({}, prev.examination);
            const sectionData = Object.assign({}, examination[section] || {});
            sectionData[field] = value;
            examination[section] = sectionData;
            return Object.assign({}, prev, { examination: examination });
        });
    }, []);

    /* ─── Multi-select toggle (for arrays like sexual_history, past_gynae_history etc.) ─── */
    const toggleArrayField = useCallback(function (fieldName, itemValue) {
        setForm(function (prev) {
            const current = prev[fieldName] || [];
            const idx = current.indexOf(itemValue);
            let next;
            if (itemValue === "none") {
                next = ["none"];
            } else if (idx >= 0) {
                next = current.filter(function (c) { return c !== itemValue; });
            } else {
                next = current.filter(function (c) { return c !== "none"; }).concat([itemValue]);
            }
            const updated = Object.assign({}, prev);
            updated[fieldName] = next;
            return updated;
        });
    }, []);

    /* ─── Investigation toggle ─── */
    const toggleInvestigation = useCallback(function (invValue) {
        setForm(function (prev) {
            const current = prev.investigations_ordered || [];
            const idx = current.indexOf(invValue);
            let next;
            if (idx >= 0) {
                next = current.filter(function (c) { return c !== invValue; });
            } else {
                next = current.concat([invValue]);
            }
            return Object.assign({}, prev, { investigations_ordered: next });
        });
    }, []);

    /* ─── Select ICD-10 diagnosis ─── */
    const selectDiagnosis = useCallback(function (diagnosisItem) {
        setForm(function (prev) {
            return Object.assign({}, prev, {
                diagnosis_icd10_code: diagnosisItem.code,
                diagnosis_icd10_label: diagnosisItem.label,
                provisional_diagnosis: prev.provisional_diagnosis || diagnosisItem.label,
            });
        });
    }, []);

    /* ─── Cycle info computed ─── */
    const cycleInfo = useMemo(function () {
        return calculateCycleInfo(form.lmp, form.cycle_length);
    }, [form.lmp, form.cycle_length]);

    /* ─── GPAL string ─── */
    const gpalString = useMemo(function () {
        const g = form.gravida || "0";
        const p = form.para || "0";
        const a = form.abortions || "0";
        const l = form.living_children || "0";
        return "G" + g + " P" + p + " A" + a + " L" + l;
    }, [form.gravida, form.para, form.abortions, form.living_children]);

    /* ─── Summary stats ─── */
    const summary = useMemo(function () {
        return {
            totalConsultations: consultations.length,
            hasCurrentVisitRecord: consultations.some(function (c) { return c.visit_id === visit_id; }),
        };
    }, [consultations, visit_id]);

    /* ─── Start edit ─── */
    const startEditRecord = useCallback(function (record) {
        setEditingRecord(record);
        /* Flatten the record into form fields */
        const mh = record.menstrual_history || {};
        const flatForm = Object.assign({}, INITIAL_FORM, {
            presenting_complaint: record.presenting_complaint || "",
            menarche_age: mh.menarche_age || "",
            cycle_length: mh.cycle_length || "28",
            period_duration: mh.period_duration || "5",
            regularity: mh.regularity || "",
            flow_amount: mh.flow_amount || "",
            dysmenorrhea: mh.dysmenorrhea || "none",
            lmp: mh.lmp || "",
            intermenstrual_bleeding: mh.intermenstrual_bleeding || false,
            post_coital_bleeding: mh.post_coital_bleeding || false,
            gravida: record.obstetric_summary ? record.obstetric_summary.match(/G(\d+)/)?.[1] || "" : "",
            para: record.obstetric_summary ? record.obstetric_summary.match(/P(\d+)/)?.[1] || "" : "",
            abortions: record.obstetric_summary ? record.obstetric_summary.match(/A(\d+)/)?.[1] || "" : "",
            living_children: record.obstetric_summary ? record.obstetric_summary.match(/L(\d+)/)?.[1] || "" : "",
            sexual_history: record.sexual_history || [],
            contraceptive_current: "",
            contraceptive_past: record.contraceptive_history || [],
            past_gynae_history: record.past_gynae_history || [],
            past_gynae_details: "",
            physical_examination: record.physical_examination || "",
            past_medical_history: record.past_medical_history || "",
            past_obstetric_history: record.past_obstetric_history || "",
            family_history: record.family_history || "",
            notes: record.notes || "",
            examination: record.examination_findings || {},
            provisional_diagnosis: record.provisional_diagnosis || "",
            diagnosis_icd10_code: record.diagnosis_icd10_code || "",
            diagnosis_icd10_label: record.diagnosis_icd10_label || "",
            differential_diagnoses: record.differential_diagnoses || "",
            investigations_ordered: record.investigations_ordered || [],
            investigation_notes: record.investigation_notes || "",
            treatment_plan: record.treatment_plan || "",
            procedure_performed: record.procedure_performed || "none",
            procedure_details: record.procedure_details || "",
            medications_prescribed: record.medications_prescribed || "",
            follow_up_plan: record.follow_up_plan || "",
            follow_up_date: record.follow_up_date || "",
            referral_needed: record.referral_needed || false,
            referral_to: record.referral_to || "",
            referral_reason: record.referral_reason || "",
        });
        setForm(flatForm);
        setActiveTab("consultation");
        setActiveSection("history");
    }, []);

    /* ─── Reset form ─── */
    const resetForm = useCallback(function () {
        setEditingRecord(null);
        setForm(Object.assign({}, INITIAL_FORM));
        setValidationErrors({});
        setActiveSection("history");
    }, []);

    /* ─── Build payload from form ─── */
    const buildPayload = useCallback(function () {
        return {
            patient_number: patient_number,
            visit_id: visit_id,
            seen_by: user_roles_id,
            presenting_complaint: form.presenting_complaint,
            menstrual_history: {
                menarche_age: form.menarche_age,
                cycle_length: form.cycle_length,
                period_duration: form.period_duration,
                regularity: form.regularity,
                flow_amount: form.flow_amount,
                dysmenorrhea: form.dysmenorrhea,
                lmp: form.lmp,
                intermenstrual_bleeding: form.intermenstrual_bleeding,
                post_coital_bleeding: form.post_coital_bleeding,
            },
            obstetric_summary: gpalString,
            sexual_history: form.sexual_history,
            contraceptive_history: form.contraceptive_past,
            past_gynae_history: form.past_gynae_history,
            physical_examination: form.physical_examination,
            past_medical_history: form.past_medical_history,
            past_obstetric_history: form.past_obstetric_history,
            family_history: form.family_history,
            notes: form.notes,
            provisional_diagnosis: form.provisional_diagnosis,
            diagnosis_icd10_code: form.diagnosis_icd10_code,
            diagnosis_icd10_label: form.diagnosis_icd10_label,
            differential_diagnoses: form.differential_diagnoses,
            investigations_ordered: form.investigations_ordered,
            investigation_notes: form.investigation_notes,
            treatment_plan: form.treatment_plan,
            procedure_performed: form.procedure_performed,
            procedure_details: form.procedure_details,
            medications_prescribed: form.medications_prescribed,
            follow_up_plan: form.follow_up_plan,
            follow_up_date: form.follow_up_date,
            referral_needed: form.referral_needed,
            referral_to: form.referral_to,
            referral_reason: form.referral_reason,
        };
    }, [form, patient_number, visit_id, user_roles_id, gpalString]);

    /* ─── Save consultation ─── */
    const saveConsultation = useCallback(function () {
        /* Run full validation */
        const isValid = validateForm();
        if (!isValid) {
            actions.snackbarActions.snackError("Please fill in all required fields");
            /* Navigate to the first section that has errors */
            const errorFields = Object.keys(REQUIRED_FIELDS).filter(function (f) {
                const val = form[f];
                return val === undefined || val === null || val === "";
            });
            if (errorFields.length > 0) {
                const firstErrorSection = REQUIRED_FIELDS[errorFields[0]].section;
                setActiveSection(firstErrorSection);
            }
            return;
        }

        setSaving(true);
        const payload = buildPayload();

        const request = editingRecord
            ? axios.put(config.smsUrl + "/obgyn/gynae/" + editingRecord.id, payload)
            : axios.post(config.smsUrl + "/obgyn/gynae/", payload);

        request
            .then(function (res) {
                if (editingRecord) {
                    const updated = Object.assign({}, editingRecord, payload, res.data || {});
                    const updatedRecords = consultations.map(function (r) {
                        return r.id === editingRecord.id ? updated : r;
                    });
                    setConsultations(updatedRecords);
                    actions.snackbarActions.snackSuccess("Consultation updated successfully");
                } else {
                    const savedId = res.data && res.data.id;
                    if (savedId) {
                        axios.get(config.smsUrl + "/obgyn/gynae/" + savedId)
                            .then(function (fullRes) {
                                const fullRecord = fullRes.data || Object.assign({}, payload, res.data);
                                setConsultations(function (prev) { return prev.concat([fullRecord]); });
                            })
                            .catch(function () {
                                setConsultations(function (prev) { return prev.concat([Object.assign({}, payload, res.data)]); });
                            });
                    } else {
                        setConsultations(function (prev) { return prev.concat([Object.assign({}, payload, res.data || {})]); });
                    }
                    actions.snackbarActions.snackSuccess("Consultation saved successfully");
                }
                resetForm();
            })
            .catch(function (err) {
                errorMessages(err, null, actions);
            })
            .finally(function () { setSaving(false); });
    }, [form, editingRecord, consultations, buildPayload, resetForm, validateForm, actions, patient_number, visit_id]);

    /* ─── Section navigation ─── */
    const FORM_SECTIONS = ["history", "examination"];

    const nextSection = useCallback(function () {
        const idx = FORM_SECTIONS.indexOf(activeSection);
        if (idx < FORM_SECTIONS.length - 1) {
            setActiveSection(FORM_SECTIONS[idx + 1]);
        }
    }, [activeSection]);

    const prevSection = useCallback(function () {
        const idx = FORM_SECTIONS.indexOf(activeSection);
        if (idx > 0) {
            setActiveSection(FORM_SECTIONS[idx - 1]);
        }
    }, [activeSection]);

    /* ─── Return ─── */
    return {
        /* Data */
        consultations: consultations,
        form: form,
        editingRecord: editingRecord,
        /* UI state */
        loading: loading,
        saving: saving,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        activeSection: activeSection,
        setActiveSection: setActiveSection,
        FORM_SECTIONS: FORM_SECTIONS,
        /* Handlers */
        handleChange: handleChange,
        handleExamChange: handleExamChange,
        toggleArrayField: toggleArrayField,
        toggleInvestigation: toggleInvestigation,
        selectDiagnosis: selectDiagnosis,
        saveConsultation: saveConsultation,
        startEditRecord: startEditRecord,
        resetForm: resetForm,
        nextSection: nextSection,
        prevSection: prevSection,
        /* Validation */
        validationErrors: validationErrors,
        validateForm: validateForm,
        clearFieldError: clearFieldError,
        REQUIRED_FIELDS: REQUIRED_FIELDS,
        /* Computed */
        cycleInfo: cycleInfo,
        gpalString: gpalString,
        summary: summary,
        /* Constants (re-export) */
        MENSTRUAL_REGULARITY: MENSTRUAL_REGULARITY,
        MENSTRUAL_FLOW: MENSTRUAL_FLOW,
        DYSMENORRHEA_SEVERITY: DYSMENORRHEA_SEVERITY,
        SEXUAL_HISTORY_OPTIONS: SEXUAL_HISTORY_OPTIONS,
        CONTRACEPTIVE_HISTORY_OPTIONS: CONTRACEPTIVE_HISTORY_OPTIONS,
        PAST_GYNAE_CONDITIONS: PAST_GYNAE_CONDITIONS,
        EXAMINATION_SECTIONS: EXAMINATION_SECTIONS,
        COMMON_GYNAE_DIAGNOSES: COMMON_GYNAE_DIAGNOSES,
        INVESTIGATION_TYPES: INVESTIGATION_TYPES,
        PROCEDURE_OPTIONS: PROCEDURE_OPTIONS,
    };
};
